import React from 'react'
import { IProps } from './index'
import { Text } from 'marketing-site/src/library/elements/Text'
import { JumpLink } from 'marketing-site/src/library/elements/JumpLink'
import { ImageOrAutoplayVideo } from 'marketing-site/src/library/elements/ImageOrAutoplayVideo'
import { containerMaxWidth, Color, mq } from 'marketing-site/src/library/utils'
import circle from './images/circle.svg'
import triangle from './images/triangle.svg'
import diamond from './images/diamond.svg'
import star from './images/star.svg'
import { useSpacing } from 'marketing-site/src/library/providers/spacingProvider'
import classNames from 'classnames'

export function CSFHero({
  eyebrow,
  heading,
  subheading,
  legend,
  image,
  video,
  videoWebm,
  jumpLink,
}: IProps): React.ReactElement {
  const spacing = useSpacing()
  function renderMedia() {
    return <ImageOrAutoplayVideo imageRef={image} video={video} videoWebm={videoWebm} />
  }

  const classes = classNames('csf-hero__wrapper', {
    'csf-hero__wrapper--padding': !spacing,
  })

  return (
    <div className={classes}>
      {jumpLink && <JumpLink {...jumpLink} />}
      <div className="csf-hero">
        <div className="csf-hero__copy">
          {eyebrow && (
            <span>
              <Text brand="default" size="caption-bold">
                {eyebrow}
              </Text>
            </span>
          )}
          <div>
            <Text brand="default" size="heading-1-default">
              {heading}
            </Text>
          </div>
          <div>
            {subheading && (
              <Text brand="default" size="heading-4">
                {subheading}
              </Text>
            )}
          </div>
        </div>
        <ul className="csf-hero__legend" aria-hidden={true}>
          {legend &&
            legend.items.map((item, index) => (
              <li className="csf-hero__legend-item" key={index}>
                <Text brand="default" size="body">
                  {item.text}
                </Text>
              </li>
            ))}
        </ul>
        <div className="csf-hero__media">{renderMedia()}</div>
      </div>
      <style jsx>{`
        .csf-hero__wrapper {
          background-color: ${Color.Stone};
          display: grid;
          grid-template-columns: repeat(14, 1fr);

          .csf-hero {
            text-align: center;
            grid-column: 2 / span 12;
            display: grid;
            grid-template-areas:
              'copy'
              'media'
              'legend';
            grid-template-columns: 1fr;
            grid-gap: 30px;
          }

          &--padding {
            .csf-hero {
              padding: 40px 0;
            }
          }

          .csf-hero__copy {
            grid-area: copy;
            & > * {
              margin-top: 10px;
            }
          }

          .csf-hero__media {
            grid-area: media;
          }

          :global(.csf-hero__media picture),
          :global(.csf-hero__media img) {
            max-width: 320px;
            margin: 0 auto;
          }

          .csf-hero__legend {
            grid-area: legend;
          }

          .csf-hero__legend-item {
            width: 85%;
            padding-left: 50px;
            padding-right: 10px;
            margin-bottom: 20px;
            margin-left: 8%;
            position: relative;
            text-align: left;

            &::before {
              content: '';
              position: absolute;
              height: 45px;
              width: 45px;
              top: 50%;
              left: 0;
              transform: translateY(-50%);
              background-repeat: no-repeat;
            }

            &:nth-of-type(1)::before {
              background-image: url(${diamond});
            }
            &:nth-of-type(2)::before {
              background-image: url(${circle});
            }
            &:nth-of-type(3)::before {
              background-image: url(${star});
            }
            &:nth-of-type(4)::before {
              background-image: url(${triangle});
            }
          }
        }

        @media (${mq.tablet}) {
          .csf-hero__wrapper {
            background-color: ${Color.Stone};

            .csf-hero {
              max-width: ${containerMaxWidth};
              margin: 0 auto;
              grid-template-areas:
                'copy media'
                'legend media';
              grid-template-columns: repeat(2, 1fr);
            }

            &--padding {
              margin-top: -250px;
              .csf-hero {
                padding: 200px 0 55px;
              }
            }
            .csf-hero__copy {
              text-align: left;

              & > * {
                margin-top: 20px;
              }
            }

            .csf-hero__media {
              grid-row: 1 / span 2;
              margin: 0 auto;
              align-self: center;
            }

            :global(.csf-hero__media picture),
            :global(.csf-hero__media img),
            :global(.csf-hero__media video) {
              width: 100%;
              max-width: 275px !important;
            }
          }
        }

        @media (${mq.laptop}) {
          .csf-hero__wrapper {
            &--padding {
              .csf-hero {
                padding-top: 250px;
              }
            }
            .csf-hero__legend {
              display: flex;
              flex-wrap: wrap;
              align-items: center;
            }
            .csf-hero__legend-item {
              width: 50%;
              margin-left: 0;
            }
            :global(.csf-hero__media picture),
            :global(.csf-hero__media img),
            :global(.csf-hero__media video) {
              max-width: 350px !important;
            }
          }
        }
        @media (${mq.laptop}) {
          .csf-hero__wrapper {
            &--padding {
              margin-bottom: 60px;
            }
            .csf-hero__media {
              margin: -40px auto 0;
            }
            :global(.csf-hero__media picture),
            :global(.csf-hero__media img),
            :global(.csf-hero__media video) {
              max-width: 450px !important;
            }
          }
        }

        @media (min-width: 1290px) {
          .csf-hero__wrapper {
            background-color: ${Color.Stone};
            color: ${Color.Slate};

            &--padding {
              margin-top: -250px;
              padding-bottom: 60px;
            }
          }
        }

        .csf-hero__wrapper {
          @media (min-width: 1330px) {
            background-position: left 55% top -250px, left top -3px;
          }
          @media (min-width: 1640px) {
            background-position: left 56% top -250px, left top -3px;
          }
          @media (min-width: 2090px) {
            background-position: left 57% top -250px, left top -3px;
          }
        }
        @media (min-width: 2400px) {
          .csf-hero__wrapper {
            background-image: none;
            background-color: ${Color.Stone};

            &--padding {
              padding-bottom: 0;
            }
          }
          .csf-hero {
            color: #000;
          }
        }
      `}</style>
    </div>
  )
}
